<template>
  <div
    :class="!isMobile() ? 'main-container-w-background' : 'main-container'"
    class="d-flex align-center justify-center"
  >
    <!-- prevent zoom on input focus -->
    <meta
      v-if="isMobile()"
      name="viewport"
      content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
    />
    <v-card
      :elevation="isMobile() ? 0 : 24"
      :class="!isMobile() ? 'pa-5 login-card' : ''"
    >
      <div class="d-flex flex-column align-center justify-center">
        <!--
          ThreadFlow brand mark. This is the only product identification on
          the page, so it is NOT decorative: it carries a real alt text and a
          data-testid so its removal shows up as a failing test rather than a
          silent debrand (see auth-surface-threadflow-branding.spec.js).

          HOST-CONDITIONAL (Unit 7, card https://trello.com/c/mq26K91V).
          Jake, 2026-09-20: the login page at the white-label host must be
          "100% generic, with NO branding at all." `v-if`, not a CSS hide:
          a hidden brand is still in the DOM and still in the page source,
          which is not generic -- it is concealed, and anyone who looks can
          tell. The default is BRANDED, so every host that is not explicitly
          configured (including every sandbox and production today) renders
          exactly what it renders now.
        -->
        <img
          v-if="showBrand"
          src="@/assets/logo/logo.png"
          alt="ThreadFlow"
          data-testid="threadflow-brand-mark"
          style="max-height: 120px; max-width: 200px"
          class="mb-5 tf-logo no_select"
        />
        <div class="d-flex align-center justify-center">
          <!--
            Second-factor prompt. Shown INSTEAD of the password form once
            the server has answered a correct password with 401 +
            mfa_required. The gate itself is server-side -- at this point
            no token exists, so a user who closes this prompt simply has
            no session.
          -->
          <v-col
            v-if="mfaChallenge"
            cols="12"
            md="6"
            sm="10"
            class="login"
            data-testid="mfa-step-up"
          >
            <v-form class="login-form" @submit.prevent="verifyMfa">
              <div class="d-flex flex-column align-center justify-center">
                <div style="width: 350px">
                  <div class="no_select d-flex justify-center mb-3">
                    Enter the 6-digit code from your authenticator app.
                  </div>
                  <v-text-field
                    v-model="mfaCode"
                    prepend-inner-icon="lock"
                    name="mfa-code"
                    type="text"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    placeholder="123456"
                    data-testid="mfa-code"
                    variant="solo"
                    rounded
                    autofocus
                  ></v-text-field>

                  <div
                    v-if="mfaError"
                    class="no_select d-flex justify-center mb-2"
                    data-testid="mfa-step-up-error"
                  >
                    {{ mfaError }}
                  </div>

                  <v-btn
                    block
                    color="primary"
                    type="submit"
                    :loading="verifyingMfa"
                    :disabled="verifyingMfa"
                    data-testid="mfa-submit"
                    class="btn-login text-white"
                    >Verify
                  </v-btn>
                  <v-btn
                    variant="text"
                    color="primary"
                    :disabled="verifyingMfa"
                    data-testid="mfa-cancel"
                    class="btn-login btn-forgot"
                    @click="cancelMfa"
                    >Back
                  </v-btn>
                  <div class="pt-3 no_select d-flex justify-center">
                    You can also use one of your recovery codes.
                  </div>
                </div>
              </div>
            </v-form>
          </v-col>

          <v-col v-else cols="12" md="6" sm="10" class="login">
            <v-form
              class="login-form"
              @submit.prevent="submitHandler"
              v-model="valid"
              ref="form"
            >
              <div class="d-flex flex-column align-center justify-center">
                <div style="width: 350px">
                  <v-card-text class="ma-0 pa-0 mb-3">
                    <v-text-field
                      prepend-inner-icon="person"
                      name="login"
                      type="text"
                      v-model="login"
                      :rules="loginRules"
                      placeholder="Username/Email..."
                      data-testid="login"
                      variant="solo"
                      rounded
                    >
                    </v-text-field>
                    <v-text-field
                      id="password"
                      prepend-inner-icon="lock"
                      name="password"
                      type="password"
                      v-model="password"
                      :rules="passwordRules"
                      placeholder="Password"
                      data-testid="password"
                      variant="solo"
                      rounded
                    >
                    </v-text-field>

                    <div
                      v-if="forgotPassword"
                      class="no_select d-flex justify-center"
                    >
                      Try again or click Forgot Password to reset it.
                    </div>
                    <v-spacer v-else></v-spacer>
                  </v-card-text>
                  <div class="text-start">
                    <v-btn
                      block
                      color="primary"
                      type="submit"
                      :loading="loading"
                      :disabled="!valid || loading || sendingEmail"
                      data-testid="submit"
                      class="btn-login text-white"
                      >Click To Login
                    </v-btn>
                    <v-btn
                      variant="text"
                      color="primary"
                      :loading="loading"
                      :disabled="!login || loading || sendingEmail"
                      @click="resetPassword"
                      class="btn-login btn-forgot"
                      >Forgot Password
                    </v-btn>
                    <div class="pt-3 no_select d-flex justify-center">
                      Don't have an account?
                      <div
                        class="ml-1"
                        style="color: #119ae9; cursor: pointer"
                        @click="signUp"
                      >
                        Sign Up
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-form>
          </v-col>
        </div>
      </div>
      <v-progress-circular
        v-if="sendingEmail"
        :indeterminate="true"
        size="25"
        width="2"
        class="loading-wheel"
      />
      <v-dialog v-model="showResetPasswordDialog" persistent max-width="500px">
        <v-card>
          <v-card-text>
            <div class="text-h6">
              A reset password link has been sent to your email. If you can't
              find it in Inbox, please check it in the Spam folder.
            </div>
          </v-card-text>
          <v-card-actions class="py-3 px-3">
            <v-spacer></v-spacer>
            <v-btn @click="closeDialog"> Okay </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import store from "@/store";
import helpers from "@/mixins/helpers";
import { authMe } from "@/services/accountService";
// The `/login/<storeUrl>` entry auto-provisions the store's SHARED shopper
// account with no credentials -- the same shared row StorefrontSessionGate
// swaps to. Record that so the storefront chrome withholds the personal-data
// links. See router/anonymousStorefrontSession.js.
import { markAnonymousStorefrontSession } from "@/router/anonymousStorefrontSession";
// Route-fork PR-R1: replace the legacy `account.role.name == 'Owner'`
// switch with the pure-function helper added in PR-R0. The helper resolves
// destination from capabilities, not role names; this also unblocks the
// new role taxonomy without touching this file again.
import { postLoginRedirect } from "@/router/postLoginRedirect";
import { navigateAfterLogin } from "@/router/navigateAfterLogin";
import { runDeeplinkImpersonation } from "@/router/deeplinkImpersonationRuntime";
import { mfaStepUp } from "./loginMfa";
// Unit 7: which host is serving this page decides whether it is branded.
import { resolvePreAuthBranding } from "@/utils/preAuthBranding";

class User {
  constructor(data) {
    this.account_id = data.account_id;
    this.token = data.token;
    this.token_type = data.token_type;
  }
}

export default {
  data() {
    return {
      // Unit 7: resolved ONCE at component creation rather than in a
      // computed. The hostname cannot change without a full page load, which
      // rebuilds this component anyway, so re-deriving it on every render
      // would be work that can never produce a different answer.
      showBrand: resolvePreAuthBranding().showBrand,
      // login can be username or user email.
      login: "",
      password: "",
      valid: false,
      loginRules: [(v) => !!v || "Login is required"],
      passwordRules: [(v) => !!v || "Password is required"],
      shopperUrl: "",
      campaignID: null,
      campaignActive: false,
      shopperAccount: null,
      loadingCustomURL: false,
      invalidShopperLink: false,
      forgotPassword: false,
      showResetPasswordDialog: false,
      sendingEmail: false,

      // MFA step-up (card https://trello.com/c/lf2Z8aOo). The server
      // answers a correct password with 401 + mfa_required when the
      // account has a second factor, and mints NO token. These hold the
      // prompt state until the second factor is supplied.
      mfaChallenge: "",
      mfaCode: "",
      mfaError: "",
      verifyingMfa: false,
    };
  },

  mixins: [helpers],

  computed: {
    loading() {
      return this.$store.getters.loading;
    },
  },

  async mounted() {
    this.$store.state.displayOwLogo = true;
    this.$store.state.displayLogoutButton = false;
    this.$store.state.backgroundColor = "#FFFFFF";

  },

  methods: {
    signUp() {
      this.$router.push('/pricing');
    },

    async submitHandler() {
      if (this.$refs.form.validate()) {
        let resp;
        try {
          resp = await this.$axios.post("/auth/login", {
            login: this.login,
            password: this.password,
          });
        } catch (err) {
          // A correct password on an MFA-enabled account arrives HERE as
          // a 401 -- the server withholds the token until the second
          // factor is proven. That is a step-up, not a failed login, so
          // it must not raise an error or offer "Forgot Password".
          if (mfaStepUp.isStepUp(err)) {
            this.mfaChallenge = mfaStepUp.challengeOf(err);
            this.mfaCode = "";
            this.mfaError = "";
            this.forgotPassword = false;
            return;
          }
          console.log(describeError(err));
          this.$store.dispatch("raiseError", err);
          this.forgotPassword = true;
          return;
        }
        this.forgotPassword = false;
        this.showResetPasswordDialog = false;
        await this.establishSession(resp.data);
      }
    },

    /**
     * Complete a login from an auth payload.
     *
     * Shared by the password path and the MFA step-up path so BOTH
     * establish the session identically. Two copies of this would be two
     * chances for one of them to skip the logout-first clear, the
     * localStorage verification, or the capability-driven redirect --
     * and the step-up path is exactly the one a reviewer is least likely
     * to exercise by hand.
     */
    async establishSession(authResponse) {
      {
        const resp = { data: authResponse };
        // clear old account information in localStorage
        await this.$store.dispatch("logout");

        // save the token so we can make the next request to get the account's data
        const cleanToken = String(resp.data.token).replace(/^["']+|["']+$/g, '');
        localStorage.setItem("token", cleanToken);
        localStorage.setItem("userId", resp.data.account_id);
        
        // CRITICAL: Verify localStorage was actually written before proceeding
        // This creates a hard synchronization point
        const verifyToken = localStorage.getItem("token");
        if (verifyToken !== cleanToken) {
          console.error('Token verification failed: stored token does not match the issued token');
          throw new Error('Token storage failed');
        }
        
        // Now safe to proceed - token is definitely in localStorage.
        //
        // P2-B: try/catch parity with loginShopperFromURL (P1-4). The token
        // is already in localStorage at this point. If jv/get rejects
        // (network blip, 5xx) and we let the rejection propagate uncaught,
        // the user lands in a half-authed state: stored token, no Vuex/Pinia
        // hydration, no redirect. Recover by dispatching `logout` (clears
        // the just-stored token + Pinia + Vuex) and surfacing the error.
        // No router.push — user remains on /login.
        try {
          const account = await this.$store.dispatch("jv/get", [
            `accounts/${resp.data.account_id}`,
            { params: { include: "role" } },
          ]);
          // save the new account information in localStorage. PR-11: pass
          // the auth response so the Pinia useAccountStore hydrates with
          // capabilities + distributor from /api/auth/login.
          //
          // #907: await the dispatch. The Vuex `login` action is currently
          // synchronous, so dispatch() resolves on the next microtask;
          // awaiting yields that boundary so Vue reactivity flushes before
          // the router.push runs. Also future-proofs against `login`
          // growing real async work later.
          // GH #2941 defect 2: the user just submitted a password, so any
          // anonymous-storefront marker left over from browsing this store
          // as a guest on the same tab must not survive into their personal
          // session. See router/anonymousStorefrontSession.js.
          await this.$store.dispatch("login", {
            account,
            authResponse: resp.data,
            credentialed: true,
          });

          // Route-fork PR-R1: dispatch via postLoginRedirect (PR-R0 helper).
          // The helper resolves a destination from capabilities, replacing
          // the legacy role-name switch. Shopper still requires a clean
          // checkout-fields reset before the redirect; everything else
          // follows the helper's mapping verbatim.
          const capabilities = Array.isArray(resp.data?.capabilities)
            ? resp.data.capabilities
            : [];
          if (capabilities.includes("shopper.shop")) {
            // reset VueX store
            this.$store.state.custom_checkout_fields = {};
          }
          const destination = postLoginRedirect({
            capabilities,
            campaignSlug: this.shopperUrl || undefined,
            // GH #3088: honour where the user was headed before the
            // auth bounce, so an emailed approval deep link survives
            // the sign-in it triggered. postLoginRedirect validates
            // this (same-origin path only) -- it arrives from a query
            // param and is attacker-controllable.
            returnTo: this.$route?.query?.next,
          });
          // Land on the deep link only if the role may view it; a guard that
          // denies it falls back to the plain role landing (no /login loop).
          // A resource deep link first seats the user as its owner (SA
          // impersonation / distributor view-as-client) or lands them on
          // their role home when no seat of theirs can see it.
          await navigateAfterLogin(
            this.$router,
            destination,
            postLoginRedirect({
              capabilities,
              campaignSlug: this.shopperUrl || undefined,
            }),
            runDeeplinkImpersonation
          );
        } catch (err) {
          // Recovery path — best-effort cleanup. Wrap logout in try/catch so
          // a logout dispatch failure doesn't block error surfacing (P2-D
          // defensive wrap).
          try {
            await this.$store.dispatch("logout");
          } catch (_logoutErr) {
            // Best-effort cleanup; recovery from a failed-rehydrate path
            // shouldn't be blocked by a logout dispatch failure.
          }
          this.$store.dispatch("raiseError", err);
          // No router.push — user is already on /login.
        }
      }
    },

    /**
     * Complete a login that the server gated on a second factor.
     *
     * Sends the challenge + the typed code (TOTP or recovery) and, on
     * success, hands the returned payload to the SAME establishSession
     * used by the password path.
     *
     * On failure the challenge is kept so the user can retry with a
     * fresh code -- the server bounds retries per challenge, so keeping
     * it here does not widen anything.
     */
    async verifyMfa() {
      this.mfaError = "";
      this.verifyingMfa = true;
      try {
        const payload = await mfaStepUp.complete(
          this.mfaChallenge,
          this.mfaCode
        );
        this.mfaChallenge = "";
        this.mfaCode = "";
        await this.establishSession(payload);
      } catch (err) {
        this.mfaError =
          err?.response?.data?.message ||
          err?.message ||
          "That code could not be verified.";
      } finally {
        this.verifyingMfa = false;
      }
    },

    /** Abandon a step-up and return to the password form. */
    cancelMfa() {
      this.mfaChallenge = "";
      this.mfaCode = "";
      this.mfaError = "";
    },

    async resetPassword() {
      try {
        this.sendingEmail = true;
        try {
          const resp = await this.$axios.post("/auth/sendResetPasswordToken", {
            email: this.login,
          });
          this.sendingEmail = false;
        } catch (err) {
          this.sendingEmail = false;
          return;
        }

        this.showResetPasswordDialog = true;
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
      }
    },

    closeDialog() {
      this.showResetPasswordDialog = false;
    },

    async loginShopperFromURL(shopperAccount) {
      const resp = await this.$axios.post(`/auth/getToken/${shopperAccount.id}`);

      // clear old account information in localStorage
      await this.$store.dispatch("logout");

      // save the token so we can make the next request to get the account's data
      const cleanToken = String(resp.data.token).replace(/^["']+|["']+$/g, '');
      localStorage.setItem("token", cleanToken);
      localStorage.setItem("userId", resp.data.account_id);

      // CRITICAL: Verify localStorage was actually written before proceeding
      const verifyToken = localStorage.getItem("token");
      if (verifyToken !== cleanToken) throw new Error('Token storage failed');

      // PR-12 (issue #901): /auth/getToken returns only {token, account_id}
      // — no capabilities. Call /auth/me with the freshly-stored token to
      // fetch the full auth payload so dispatch can hydrate Pinia. Run in
      // parallel with accounts/:id to keep latency to a single round-trip.
      //
      // P1-4: try/catch is mandatory here — the token is already in
      // localStorage at this point. If Promise.all rejects (network blip,
      // 5xx) and we let the rejection propagate uncaught, the user lands
      // in a half-authed state: stored token, no Pinia hydration, no Vuex
      // account, no redirect. Recover by dispatching `logout` (clears the
      // just-stored token + Pinia + Vuex) and surfacing the error.
      try {
        const [meResp, account] = await Promise.all([
          authMe(),
          // Hydrate the account this session ACTUALLY holds. Under
          // per-visitor anonymous accounts `/auth/getToken/<id>` treats
          // the id as a TEMPLATE naming the store and returns a
          // DIFFERENT, per-visitor row -- so `shopperAccount.id` is not
          // the session's account, and requesting it would fetch a row
          // this bearer has no access to.
          this.$store.dispatch("jv/get", [
            `accounts/${resp.data.account_id}`,
            { params: { include: "role" } },
          ]),
        ]);

        // save the new account information in localStorage and hydrate Pinia
        // #907: await the dispatch. The Vuex `login` action is currently
        // synchronous, so dispatch() resolves on the next microtask;
        // awaiting yields that boundary so Vue reactivity flushes before
        // the shopper router.push runs. Future-proofs against async login.
        await this.$store.dispatch("login", { account, authResponse: meResp.data });

        // This session is the store's SHARED auto-provisioned shopper: this
        // path asked for no credentials, it just resolved the store's
        // role_id=5 account and took its token.
        //
        // Marked HERE, after the dispatches, and not earlier: the `logout`
        // dispatch above (which clears the old account) also clears this
        // marker, so a mark written before it would be silently wiped.
        markAnonymousStorefrontSession();

        if (account.role_id == 5) {
          // reset VueX store
          this.$store.state.custom_checkout_fields = {};
          if (this.shopperUrl) {
            this.$router.push(`/shop/${this.shopperUrl}`);
          } else {
            // Defensive fallback -- loginShopperFromURL is only reached via
            // the custom-shopper-link mount path (mounted() guards on
            // this.$route.params.id before setting this.shopperUrl and
            // calling this method), so this.shopperUrl should always be
            // truthy here in practice. If it ever isn't, there is no
            // sensible store-less destination (Phase D, ADR 0059, retired
            // the bare `/shop` landing): mirror postLoginRedirect()'s
            // documented rule -- "shopper with no campaignSlug falls
            // through to /login" -- rather than bouncing through a redirect
            // that itself falls back to /login anyway.
            this.$router.push("/login");
          }
        }
      } catch (err) {
        // Recovery path — best-effort cleanup. Wrap logout in try/catch so a
        // logout dispatch failure doesn't prevent us from clearing the stuck
        // loader and routing the user back to /login (P2-D defensive wrap).
        try {
          await this.$store.dispatch("logout");
        } catch (_logoutErr) {
          // Best-effort cleanup; recovery from a failed-rehydrate path
          // shouldn't be blocked by a logout dispatch failure.
        }
        // P2-A: reset loadingCustomURL so the shopper-URL UI doesn't get
        // stuck on the loading spinner, then route to /login so the user
        // isn't stranded.
        this.loadingCustomURL = false;
        this.$store.dispatch("raiseError", err);
        this.$router.push("/login");
      }
    },

    async getIsCampaignActive(id) {
      let result;
      try {
        result = await this.$axios.get(`/campaign/getIsCampaignActive/${id}`);
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
      } finally {
        if (result.data) {
          this.campaignActive = result.data[0];
        }
      }
    },

    async getCampaignIDFromShopperURL(url) {
      let result;
      try {
        result = await this.$axios.get(
          `/campaign/getCampaignIdFromShopperURL/${url}`
        );
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
      } finally {
        if (result.data) {
          this.campaignID = result.data[0];
        }
      }
    },

    async getShopperAccount(campaignID) {
      let result;
      try {
        result = await this.$axios.get(
          `/campaign/getShopperFromCampaignID/${campaignID}`
        );
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
      } finally {
        this.shopperAccount = result.data;
      }
    },
  },
};
</script>

<style scoped>
.login {
  z-index: 100;
}

.background {
  position: absolute;
  width: 100%;
  height: 100%;
}

.background-pos {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

@media only screen and (max-width: 767px) {
  .background-pos {
    display: none;
  }
}

.background-img {
  position: absolute;
  left: 0;
  height: 100vh;
}

.btn-login {
  height: 48px !important;
  border-radius: 50px !important;
}
.login {
  display: flex;
  justify-content: center;
}
.login-form {
  width: 90%;
}

.main-container-w-background {
  height: 100vh;
  background-color: #f5f8fa;
}

.main-container {
  height: 100%;
  width: 100%;
}

.login-card {
  border-radius: 36px;
  padding: 50px 36px 50px 36px;
}

.tf-logo {
  image-rendering: crisp-edges;
}

.btn-forgot {
  width: 100%;
  margin-left: 0;
}
.loading-wheel {
  height: 25px;
  width: 25px;
  position: absolute;
  right: 50px;
  bottom: 45px;
}
</style>
